<script setup lang="ts">
import { computed } from 'vue';
import { thumbUrl } from '@/utils/assetUrl';
import type { ResearchNodeView, ResearchStatus } from '@/types/research';
import ResourceCostList from '@/components/shared/ResourceCostList.vue';
import PrerequisiteList from '@/components/shared/PrerequisiteList.vue';
import type { CostLine, PrereqLine } from '@/types/constructible';

/**
 * Carte DETAIL d'une recherche selectionnee dans l'arbre : image, niveau,
 * prerequis (avec etat rempli/manquant), cout du prochain niveau et bouton
 * Lancer. Verrouillee -> liste les prerequis manquants, pas de bouton.
 * readonly (vue globale sans planete chantier) -> pas de bouton Lancer.
 */
const props = withDefaults(
  defineProps<{
    node: ResearchNodeView;
    availableByResourceId?: Record<number, number>;
    playerFlouz?: number;
    readonly?: boolean;
  }>(),
  { readonly: false },
);
const emit = defineEmits<{ start: [node: ResearchNodeView] }>();

// Dossier de représentation (wiki §24.7-ter.8) : image de bâtiment réutilisée ou symbole.
const img = computed(() =>
  props.node.imageCode
    ? thumbUrl(props.node.imageFolder || 'research_symbols', props.node.imageCode, 128)
    : null,
);

const statusTone = computed<'success' | 'warning' | 'info' | 'danger'>(() => {
  const m: Record<ResearchStatus, 'success' | 'warning' | 'info' | 'danger'> = {
    Available: 'info', InProgress: 'warning', Maxed: 'success', Locked: 'danger',
  };
  return m[props.node.status];
});
const statusLabel = computed<string>(() => {
  const m: Record<ResearchStatus, string> = {
    Available: 'Disponible', InProgress: 'En cours', Maxed: 'Niveau max', Locked: 'Verrouillé',
  };
  return m[props.node.status];
});

const progressPct = computed(() =>
  Math.round((props.node.currentLevel / props.node.maxLevel) * 100),
);

function formatSeconds(s: number): string {
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h${Math.floor((s % 3600) / 60)}m`;
}
function availableOf(resourceId: number): number {
  return props.availableByResourceId?.[resourceId] ?? 0;
}

// Coût générique (recherche = puits flouz → flouz CONSERVÉ en ligne de coût).
const costGeneric = computed<CostLine[]>(() => {
  const c = props.node.nextLevelCost;
  if (!c) return [];
  const lines: CostLine[] = [];
  if (c.flouz > 0) lines.push({ code: 'flouz', label: 'Flouz', amount: c.flouz, available: props.playerFlouz });
  for (const r of c.resources) {
    lines.push({ code: r.code, label: r.label, amount: r.amount, available: props.availableByResourceId?.[r.resourceId] });
  }
  return lines;
});
// Prérequis (tous, état satisfait/manquant rendu par le composant).
const prereqLines = computed<PrereqLine[]>(() =>
  props.node.prerequisites.map((p) => ({
    kind: 'research',
    label: p.requiredName,
    currentLevel: p.currentLevel,
    requiredLevel: p.minLevel,
    satisfied: p.satisfied,
  })),
);

const insufficientResources = computed<boolean>(() => {
  const cost = props.node.nextLevelCost;
  if (!cost || !props.availableByResourceId) return false;
  return cost.resources.some((r) => r.amount > availableOf(r.resourceId));
});
const insufficientFlouz = computed<boolean>(() => {
  const cost = props.node.nextLevelCost;
  if (!cost || props.playerFlouz === undefined) return false;
  return cost.flouz > props.playerFlouz;
});
const canStart = computed(() =>
  !props.readonly
  && props.node.status === 'Available'
  && !insufficientResources.value
  && !insufficientFlouz.value,
);
const cannotStartReason = computed<string>(() => {
  if (insufficientFlouz.value) return 'Solde flouz insuffisant';
  if (insufficientResources.value) return 'Ressources insuffisantes sur la planète';
  return '';
});
</script>

<template>
  <div class="research-node">
    <div class="research-node-top">
      <div class="research-node-thumb">
        <img v-if="img" :src="img" :alt="node.name" loading="lazy" />
        <el-icon v-else class="research-node-thumb-fallback"><Cpu /></el-icon>
      </div>
      <div class="research-node-title">
        <strong>{{ node.name }}</strong>
        <span class="research-node-code">{{ node.code }}</span>
        <el-tag :type="statusTone" size="small" effect="dark" class="research-node-status">
          {{ statusLabel }}
        </el-tag>
      </div>
    </div>

    <div class="research-node-level">
      <span class="text-mono">
        N{{ node.currentLevel }}
        <AppTooltip v-if="node.globalLevel > node.currentLevel"
              :content="`Niveau global connu par le joueur : ${node.globalLevel}`">
          <span class="global-badge">
            / Global N{{ node.globalLevel }}
          </span>
        </AppTooltip>
        <span class="cap"> / {{ node.maxLevel }}</span>
      </span>
      <el-progress :percentage="progressPct" :show-text="false" :stroke-width="4" />
    </div>

    <AppTooltip v-if="node.catchUp"
         content="Tech déjà connue globalement : coût réduit de 50%">
      <div class="research-node-catchup">
        Rattrapage -50%
      </div>
    </AppTooltip>

    <!-- Prerequis (composant partagé) -->
    <PrerequisiteList v-if="prereqLines.length" :items="prereqLines" title="Prérequis" />

    <div v-if="node.nextLevelCost" class="research-node-cost">
      <ResourceCostList :lines="costGeneric" mode="have-need" />
      <AppTooltip :content="`Durée estimée niveau ${node.currentLevel + 1}`">
        <span class="cost-badge cost-seconds">
          ⏱ {{ formatSeconds(node.nextLevelCost.seconds) }}
        </span>
      </AppTooltip>
    </div>

    <el-button
      v-if="!readonly && (node.status === 'Available' || node.status === 'Locked')"
      type="primary"
      :icon="'Top'"
      :disabled="!canStart"
      :title="node.status === 'Locked' ? 'Prérequis non remplis' : cannotStartReason"
      @click="emit('start', node)"
    >
      Lancer niveau {{ node.currentLevel + 1 }}
    </el-button>
  </div>
</template>

<style lang="scss" scoped>
.research-node {
  @include card-dark;
  display: flex;
  flex-direction: column;
  gap: $space-3;
  padding: $space-4;
}

.research-node-top {
  display: flex;
  gap: $space-3;
  align-items: center;
}
.research-node-thumb {
  flex: 0 0 auto;
  width: 56px;
  height: 56px;
  border-radius: $radius-md;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  img { width: 100%; height: 100%; object-fit: cover; }
  .research-node-thumb-fallback { font-size: 26px; color: $color-neon-cyan; }
}
.research-node-title {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.research-node-code {
  font-family: $font-mono;
  font-size: $font-size-xs;
  color: $text-muted;
}
.research-node-status { align-self: flex-start; margin-top: 2px; }

.research-node-level {
  display: flex;
  flex-direction: column;
  gap: $space-1;
  font-size: $font-size-sm;
  color: $text-secondary;
}

.research-node-prereqs {
  font-size: $font-size-xs;
  ul { list-style: none; margin: $space-1 0 0; padding: 0; display: flex; flex-direction: column; gap: 2px; }
  li {
    display: flex;
    align-items: center;
    gap: 4px;
    .text-mono { margin-left: auto; opacity: 0.8; }
    &.ok { color: $color-success; }
    &.ko { color: $color-danger; }
  }
}
.research-node-prereqs-title {
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: $text-muted;
  font-size: 10px;
}

.research-node-cost {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
.global-badge { color: $accent-legendary; font-family: $font-mono; font-size: $font-size-xs; margin-left: 4px; }
.cap { color: $text-muted; margin-left: 2px; }

.research-node-catchup {
  align-self: flex-start;
  font-size: 11px;
  font-family: $font-mono;
  padding: 2px 6px;
  border-radius: $radius-sm;
  background: rgba($color-success, 0.15);
  border: 1px solid rgba($color-success, 0.45);
  color: $color-success;
}
</style>
